// ===========================================
// BUSTED! BLACKJACK - FEEDBACK MODAL
// ===========================================

import { useState } from 'react';
import { api } from '../api/client';
import { Modal } from './Modal';

interface FeedbackModalProps {
  type: 'feedback' | 'support';
  onClose: () => void;
}

export function FeedbackModal({ type, onClose }: FeedbackModalProps) {
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const title = type === 'feedback' ? 'Send Feedback' : 'Contact Support';
  const maxLength = 2000;
  const minLength = 10;
  const canSubmit = message.trim().length >= minLength && rating >= 1 && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(null);

    const result = await api.feedback.submit(
      type,
      message.trim(),
      rating,
      type === 'support' ? contactEmail.trim() || undefined : undefined,
      type === 'support' ? contactPhone.trim() || undefined : undefined
    );

    if (result.success) {
      setIsSuccess(true);
    } else {
      setError(result.error?.message || 'Failed to submit. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      variant="default"
      blocking={true}
      title={title}
      onClose={onClose}
      maxWidthClass="max-w-lg"
    >
      {/* Content */}
      <div className="p-6">
        {isSuccess ? (
          <div className="text-center py-4">
            <p className="text-casino-green-400 text-lg font-semibold mb-2">
              Thank you! We've received your message.
            </p>
            <button
              onClick={onClose}
              className="mt-4 px-6 py-2 bg-casino-gold-500 text-gray-900 rounded-lg font-semibold hover:bg-casino-gold-400 transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Star Rating */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Rating <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredStar(star)}
                    onMouseLeave={() => setHoveredStar(0)}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <svg
                      className={`w-8 h-8 transition-colors ${
                        star <= (hoveredStar || rating)
                          ? 'text-casino-gold-500'
                          : 'text-gray-600'
                      }`}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  </button>
                ))}
              </div>
              {rating === 0 && (
                <p className="text-xs text-gray-400 mt-1">Please select a rating</p>
              )}
            </div>

            {/* Contact Fields (support only) */}
            {type === 'support' && (
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-300 mb-3">How can we contact you if necessary?</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Email</label>
                    <input
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      placeholder="your@email.com"
                      maxLength={255}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-white placeholder-gray-400 focus:outline-none focus:border-casino-gold-500 focus:ring-1 focus:ring-casino-gold-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      maxLength={50}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-white placeholder-gray-400 focus:outline-none focus:border-casino-gold-500 focus:ring-1 focus:ring-casino-gold-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Message */}
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, maxLength))}
                placeholder={
                  type === 'feedback'
                    ? 'Tell us what you think...'
                    : 'Describe your issue...'
                }
                rows={6}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-white placeholder-gray-400 resize-none focus:outline-none focus:border-casino-gold-500 focus:ring-1 focus:ring-casino-gold-500"
              />
            </div>

            {/* Character counter */}
            <div className="flex justify-between items-center mt-2 text-xs text-gray-400">
              <span>
                {message.trim().length < minLength
                  ? `Minimum ${minLength} characters`
                  : '\u00A0'}
              </span>
              <span>{message.length}/{maxLength}</span>
            </div>

            {/* Error */}
            {error && (
              <div className="mt-3 bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm text-center">
                {error}
              </div>
            )}

            {/* Submit button */}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`w-full mt-4 py-3 rounded-lg font-semibold transition-colors ${
                canSubmit
                  ? 'bg-casino-gold-500 text-gray-900 hover:bg-casino-gold-400'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>

            {/* Required field indicator */}
            <p className="text-xs text-gray-500 mt-3 text-center">
              <span className="text-red-500">*</span> Required field
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}

export default FeedbackModal;
